import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { lotesService } from '../../services/lotesService';
import { useToast } from '../../hooks/useToast';
import { useOrganizador } from '../../contexts/OrganizadorContext';

const TiposAnimais = () => {
  const { organizadorAtivo } = useOrganizador();
  const toast = useToast();
  
  const [tipos, setTipos] = useState([]);
  const [idades, setIdades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    sexo_padrao: '',
    idade_padrao_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarDados(currentPage, search);
    }
  }, [organizadorAtivo, currentPage, search]);

  const carregarDados = async (page = 1, searchTerm = '') => {
    try {
      setLoading(true);
      const [tiposData, idadesData] = await Promise.all([
        lotesService.listarTiposAnimais(organizadorAtivo.id),
        lotesService.listarIdades(organizadorAtivo.id)
      ]);
      
      let filteredData = tiposData || [];
      
      // Filtrar por busca
      if (searchTerm) {
        filteredData = filteredData.filter(tipo => 
          tipo.nome.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // Calcular paginação
      const total = filteredData.length;
      const totalPgs = Math.ceil(total / itemsPerPage);
      const startIndex = (page - 1) * itemsPerPage;
      const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);
      
      setTipos(paginatedData);
      setIdades(idadesData || []);
      setTotalPages(totalPgs);
      setCurrentPage(page);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setTipos([]);
      setIdades([]);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = (tipo) => {
    const acao = tipo.ativo ? 'inativar' : 'ativar';
    setConfirmAction({
      type: 'toggle',
      tipo,
      message: `Tem certeza que deseja ${acao} o tipo "${tipo.nome}"?`,
      action: async () => {
        try {
          const result = await lotesService.alternarStatusTipoAnimal(organizadorAtivo.id, tipo.id);
          toast.success(result.message);
          carregarDados(currentPage, search);
        } catch (error) {
          console.error('Erro ao alterar status:', error);
          toast.error('Erro ao alterar status do tipo');
        }
      }
    });
  };

  const handleDelete = (tipo) => {
    setConfirmAction({
      type: 'delete',
      tipo,
      message: `ATENÇÃO: Excluir PERMANENTEMENTE o tipo "${tipo.nome}"?`,
      subtitle: 'Esta ação não pode ser desfeita!',
      action: async () => {
        try {
          const result = await lotesService.excluirTipoAnimal(organizadorAtivo.id, tipo.id);
          toast.success(result.message);
          carregarDados(1, search);
        } catch (error) {
          console.error('Erro ao excluir tipo:', error);
          const message = error.response?.data?.detail || 'Erro ao excluir tipo';
          toast.error(message);
        }
      }
    });
  };

  const executeConfirmAction = async () => {
    if (confirmAction?.action) {
      await confirmAction.action();
    }
    setConfirmAction(null);
  };

  const cancelConfirmAction = () => {
    setConfirmAction(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    setSaving(true);
    try {
      if (editingTipo) {
        await lotesService.atualizarTipoAnimal(organizadorAtivo.id, editingTipo.id, {
          nome: formData.nome.trim(),
          sexo_padrao: formData.sexo_padrao || null,
          idade_padrao_id: formData.idade_padrao_id || null
        });
        toast.success('Tipo atualizado com sucesso!');
      } else {
        await lotesService.criarTipoAnimal(organizadorAtivo.id, {
          nome: formData.nome.trim(),
          sexo_padrao: formData.sexo_padrao || null,
          idade_padrao_id: formData.idade_padrao_id || null
        });
        toast.success('Tipo criado com sucesso!');
      }
      
      fecharModal();
      carregarDados(1, search);
    } catch (error) {
      console.error('Erro ao salvar tipo:', error);
      toast.error('Erro ao salvar tipo');
    } finally {
      setSaving(false);
    }
  };

  const abrirModal = (tipo = null) => {
    if (tipo) {
      setEditingTipo(tipo);
      setFormData({
        nome: tipo.nome,
        sexo_padrao: tipo.sexo_padrao || '',
        idade_padrao_id: tipo.idade_padrao_id?.toString() || ''
      });
    } else {
      setEditingTipo(null);
      setFormData({ nome: '', sexo_padrao: '', idade_padrao_id: '' });
    }
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setEditingTipo(null);
    setFormData({ nome: '', sexo_padrao: '', idade_padrao_id: '' });
  };

  if (loading || !organizadorAtivo) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tipos de Animais</h1>
          <p className="text-gray-600 mt-1">Gerencie os tipos de animais com configurações padrão</p>
        </div>
        <button
          onClick={() => abrirModal()}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Novo Tipo
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border p-4">
        <input
          type="text"
          placeholder="Buscar tipos de animais..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {tipos.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sexo Padrão
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Idade Padrão
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tipos.map((tipo) => (
                <tr key={tipo.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className={`font-medium ${
                        tipo.ativo ? 'text-gray-900' : 'text-gray-400 line-through'
                      }`}>
                        {tipo.nome}
                      </div>
                      {!tipo.ativo && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                          Inativo
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {tipo.sexo_padrao || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {idades.find(i => i.id === tipo.idade_padrao_id)?.nome || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1">
                      <button
                        onClick={() => abrirModal(tipo)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(tipo)}
                        className={`p-1 rounded transition-colors ${
                          tipo.ativo 
                            ? 'text-yellow-600 hover:text-yellow-800' 
                            : 'text-green-600 hover:text-green-800'
                        }`}
                        title={tipo.ativo ? 'Inativar' : 'Ativar'}
                      >
                        {tipo.ativo ? '⏸️' : '▶️'}
                      </button>
                      <button
                        onClick={() => handleDelete(tipo)}
                        className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                        title="Excluir permanentemente"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">Nenhum tipo cadastrado</div>
            <p className="text-sm text-gray-400 mb-6">
              Cadastre os tipos de animais para facilitar a criação de lotes
            </p>
            <button
              onClick={() => abrirModal()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Cadastrar Primeiro Tipo
            </button>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg border p-4 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Anterior
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 border rounded ${
                  currentPage === page 
                    ? 'bg-blue-600 text-white border-blue-600' 
                    : 'hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Toast Messages */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toast.toasts.map((t) => (
          <div
            key={t.id}
            className={`px-4 py-2 rounded-lg shadow-lg text-white ${
              t.type === 'success' ? 'bg-green-500' :
              t.type === 'error' ? 'bg-red-500' :
              t.type === 'warning' ? 'bg-yellow-500' :
              'bg-blue-500'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className={`text-xl font-semibold mb-4 ${
              confirmAction.type === 'delete' ? 'text-red-600' : 'text-yellow-600'
            }`}>
              {confirmAction.type === 'delete' ? '⚠️ Confirmar Exclusão' : '❓ Confirmar Ação'}
            </h2>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">{confirmAction.message}</p>
              {confirmAction.subtitle && (
                <p className="text-red-600 text-sm font-medium">{confirmAction.subtitle}</p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={cancelConfirmAction}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={executeConfirmAction}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${
                  confirmAction.type === 'delete' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-yellow-600 hover:bg-yellow-700'
                }`}
              >
                {confirmAction.type === 'delete' ? 'Excluir' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">
              {editingTipo ? 'Editar Tipo' : 'Novo Tipo de Animal'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Bezerro, Garrote, Vaca..."
                  required
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sexo Padrão
                </label>
                <select
                  value={formData.sexo_padrao}
                  onChange={(e) => setFormData(prev => ({ ...prev, sexo_padrao: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={saving}
                >
                  <option value="">Selecione...</option>
                  <option value="Macho">Macho</option>
                  <option value="Fêmea">Fêmea</option>
                  <option value="Misto">Misto</option>
                  <option value="Prendas">Prendas</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Idade Padrão
                </label>
                <select
                  value={formData.idade_padrao_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, idade_padrao_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={saving}
                >
                  <option value="">Selecione...</option>
                  {idades.map(idade => (
                    <option key={idade.id} value={idade.id}>{idade.nome}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={fecharModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TiposAnimais;